import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  X, Check, Loader2, Plus, Trash2, Pencil, Copy, AlertTriangle, MapPin, Euro,
  ChevronLeft, ChevronRight, CheckCircle2, RefreshCcw, CalendarCheck, Sparkles,
} from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { i18nLocale } from "../lib/locale";
import { LANG_CODES, langName } from "../i18n/languages";

const SOFTWARES = ["Winpharma", "LGPI", "Smart RX", "Pharmaland", "Leo",
  "Fiducial Pharma", "Alliance Premium", "Pharmagest", "Caduciel",
  "ProPharmaX", "Tactil", "TriaPharm", "pharma4", "pharmacie.cloud"];
const NEW_MISSIONS = ["Vaccination", "Tests de dépistage (TROD angine)", "Tests de dépistage (TROD cystite)",
  "Prescription", "Entretiens pharmaceutiques",
  "Tests allergies", "Tests cholestérol", "Otoscopie", "Test glycémie"];
const ROLES = [
  "Pharmacien",
  "Préparateur en pharmacie",
  "Étudiant en pharmacie",
  "Conseiller dermo-cosmétique",
  "Apprenti préparateur",
  "Diététicien",
  "Opticien",
  "Naturopathe",
  "Orthopédiste",
  "Rayonniste",
  "Administratif / Secrétaire",
  "Agent d'entretien",
  "Responsable des achats",
  "Chauffeur livreur",
  "Gestionnaire tiers payant",
  "Audioprothésiste",
];
const WEEKDAYS = [
  { key: "monday",    label: "Lundi" },
  { key: "tuesday",   label: "Mardi" },
  { key: "wednesday", label: "Mercredi" },
  { key: "thursday",  label: "Jeudi" },
  { key: "friday",    label: "Vendredi" },
  { key: "saturday",  label: "Samedi" },
  { key: "sunday",    label: "Dimanche" },
];
const DEFAULT_SLOTS = () => [
  { start: "09:00", end: "12:00" },
  { start: "15:00", end: "17:00" },
];

const TYPE_HOURS = {
  full:      { start: "09:00", end: "17:00", label: "Journée complète" },
  morning:   { start: "09:00", end: "13:00", label: "Matin" },
  afternoon: { start: "13:00", end: "17:00", label: "Après-midi" },
};
const CAL_WD = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const isoDate = (date) => {
  const d = new Date(date);
  // local YYYY-MM-DD (avoid timezone shifts when sending to backend)
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const defaultForm = () => ({
  mode: "temporary",
  weekdays: [],
  dates: [],                        // ISO strings
  per_day: {},                      // { keyOrDate: { slots, role_type?, skills? } }
  role_type: "Pharmacien",
  skills: [],
  softwares: [],
  new_missions: [],
  languages: [],
  address: "",
  hourly_rate: 0,
  notes: "",
  urgent: false,
});

export default function NewNeedModal({ open, onClose, onCreated, defaultAddress = "", existing = null, onSwitchMode }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const pharmacyAddress = user?.address || defaultAddress || "";
  const editing = !!existing;
  const [form, setForm] = useState(() => ({ ...defaultForm(), address: pharmacyAddress }));
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [applyOpen, setApplyOpen] = useState(null); // key of date showing the bulk-apply panel
  const [focusedKey, setFocusedKey] = useState(null); // currently highlighted day card
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [matchPreview, setMatchPreview] = useState(null);
  const [pending, setPending] = useState(new Set());
  const [hoursOpen, setHoursOpen] = useState(false);

  // Live indicator: how many candidates are available on the selected dates
  useEffect(() => {
    if (form.mode !== "temporary" || form.dates.length === 0) {
      setMatchPreview(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post("/needs/preview-match", { dates: form.dates });
        if (!cancelled) setMatchPreview(data?.available_candidates ?? null);
      } catch {
        if (!cancelled) setMatchPreview(null);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [form.mode, form.dates]);

  // When opening in edit mode, hydrate form from the existing need
  useEffect(() => {
    if (existing) {
      const m = existing.mode || (existing.dates?.length ? "temporary" : "permanent");
      const gs = (existing.global_slots || [])[0];
      const pd = { ...(existing.per_day || {}) };
      if (m === "temporary") {
        (existing.dates || []).forEach((d) => {
          if (!pd[d]?.slots?.length) pd[d] = { ...(pd[d] || {}), slots: gs ? [{ start: gs.start, end: gs.end }] : DEFAULT_SLOTS() };
        });
      }
      setForm({
        mode: m,
        weekdays: existing.weekdays || [],
        dates: existing.dates || [],
        per_day: pd,
        role_type: existing.role_type || "Pharmacien",
        skills: existing.skills || [],
        softwares: existing.softwares || [],
        new_missions: existing.new_missions || [],
        languages: existing.languages || [],
        address: pharmacyAddress || existing.address || "",
        hourly_rate: existing.hourly_rate || 0,
        notes: existing.notes || "",
        urgent: !!existing.urgent,
      });
      // Position the calendar on the first selected date so existing dates are visible immediately
      const firstIso = (existing.dates || []).slice().sort()[0];
      if (firstIso) setCalendarMonth(new Date(`${firstIso}T00:00:00`));
    } else if (open) {
      setForm({ ...defaultForm(), address: pharmacyAddress });
      setCalendarMonth(new Date());
      setFocusedKey(null);
      setPending(new Set());
      setHoursOpen(false);
    }
  }, [existing, open, pharmacyAddress]);

  const monthCells = useMemo(() => {
    const first = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
    const firstWd = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstWd; i++) {
      cells.push({ date: new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), -firstWd + i + 1), muted: true });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ date: new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), d), muted: false });
    }
    while (cells.length % 7 !== 0) {
      const last = cells[cells.length - 1].date;
      cells.push({ date: new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1), muted: true });
    }
    return cells;
  }, [calendarMonth]);

  const dayKeys = useMemo(() => {
    if (form.mode === "permanent") {
      const order = WEEKDAYS.map((w) => w.key);
      return [...form.weekdays].sort((a, b) => order.indexOf(a) - order.indexOf(b));
    }
    return [...form.dates].sort();
  }, [form.mode, form.weekdays, form.dates]);

  if (!open) return null;

  const dayLabel = (key) => {
    if (form.mode === "permanent") return WEEKDAYS.find((w) => w.key === key)?.label || key;
    try {
      const d = new Date(`${key}T00:00:00`);
      return d.toLocaleDateString(i18nLocale(), { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    } catch { return key; }
  };

  const slotsFor = (key) => form.per_day[key]?.slots || DEFAULT_SLOTS();

  const setSlots = (key, slots) =>
    setForm((f) => ({
      ...f,
      per_day: { ...f.per_day, [key]: { ...(f.per_day[key] || {}), slots } },
    }));

  const updateSlot = (key, idx, field, val) => {
    const slots = slotsFor(key).map((s, i) => (i === idx ? { ...s, [field]: val } : s));
    setSlots(key, slots);
  };
  const addSlot = (key) => setSlots(key, [...slotsFor(key), { start: "09:00", end: "12:00" }]);
  const removeSlot = (key, idx) => {
    const next = slotsFor(key).filter((_, i) => i !== idx);
    setSlots(key, next.length ? next : DEFAULT_SLOTS());
  };

  const applySlotsToOthers = (sourceKey, targetKeys) => {
    const src = slotsFor(sourceKey);
    setForm((f) => {
      const per_day = { ...f.per_day };
      targetKeys.forEach((k) => {
        per_day[k] = { ...(per_day[k] || {}), slots: src.map((s) => ({ ...s })) };
      });
      return { ...f, per_day };
    });
    setApplyOpen(null);
  };

  // ---- Temporary mode: candidate-availability-like calendar behaviour ----
  const todayIso = isoDate(new Date());

  const togglePending = (k) =>
    setPending((p) => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const applyHours = ({ type, start, end }) => {
    setForm((f) => {
      const per_day = { ...f.per_day };
      const dates = new Set(f.dates);
      pending.forEach((k) => { per_day[k] = { slots: [{ start, end }], type }; dates.add(k); });
      return { ...f, dates: Array.from(dates).sort(), per_day };
    });
    setPending(new Set());
    setHoursOpen(false);
  };

  const removeDate = (k) =>
    setForm((f) => {
      const per_day = { ...f.per_day };
      delete per_day[k];
      return { ...f, dates: f.dates.filter((d) => d !== k), per_day };
    });

  const applyFullTo = (keys) => {
    if (keys.length === 0) return;
    setForm((f) => {
      const per_day = { ...f.per_day };
      const dates = new Set(f.dates);
      keys.forEach((k) => {
        per_day[k] = { slots: [{ start: TYPE_HOURS.full.start, end: TYPE_HOURS.full.end }], type: "full" };
        dates.add(k);
      });
      return { ...f, dates: Array.from(dates).sort(), per_day };
    });
  };
  const quickWeek = () => {
    const monday = new Date();
    monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
    const keys = [];
    for (let i = 0; i < 5; i++) {
      const d = new Date(monday); d.setDate(monday.getDate() + i);
      const k = isoDate(d);
      if (k >= todayIso) keys.push(k);
    }
    applyFullTo(keys);
  };
  const quickMonth = () => {
    const keys = [];
    monthCells.forEach(({ date, muted }) => {
      if (muted) return;
      const k = isoDate(date);
      const wd = date.getDay();
      if (k >= todayIso && wd !== 0 && wd !== 6) keys.push(k);
    });
    applyFullTo(keys);
  };
  const resetMonth = () => {
    const ym = isoDate(calendarMonth).slice(0, 7);
    setForm((f) => {
      const per_day = { ...f.per_day };
      f.dates.forEach((d) => { if (d.startsWith(ym)) delete per_day[d]; });
      return { ...f, dates: f.dates.filter((d) => !d.startsWith(ym)), per_day };
    });
    setPending(new Set());
  };

  const toggleWeekday = (key) => {
    setForm((f) => {
      const has = f.weekdays.includes(key);
      const weekdays = has ? f.weekdays.filter((k) => k !== key) : [...f.weekdays, key];
      const per_day = { ...f.per_day };
      if (has) delete per_day[key];
      else if (!per_day[key]) per_day[key] = { slots: DEFAULT_SLOTS() };
      return { ...f, weekdays, per_day };
    });
  };

  const toggleIn = (field, val) =>
    setForm((f) => ({
      ...f,
      [field]: f[field].includes(val) ? f[field].filter((x) => x !== val) : [...f[field], val],
    }));

  const submit = async () => {
    setErr("");
    if (dayKeys.length === 0) {
      if (form.mode === "temporary" && pending.size > 0) { setHoursOpen(true); return; }
      setErr(form.mode === "permanent" ? "Sélectionnez au moins un jour." : "Sélectionnez au moins une date, puis cliquez sur « Ajouter » pour définir les horaires.");
      return;
    }
    if (!form.address.trim()) {
      setErr("Adresse requise.");
      return;
    }
    setBusy(true);
    try {
      const firstKey = dayKeys[0];
      const globalSlots = form.mode === "temporary"
        ? (form.per_day[firstKey]?.slots || DEFAULT_SLOTS())
        : slotsFor(firstKey);
      const payload = {
        mode: form.mode,
        weekdays: form.mode === "permanent" ? form.weekdays : [],
        dates: form.mode === "temporary" ? form.dates : [],
        global_slots: globalSlots,
        per_day: form.per_day,
        role_type: form.role_type,
        skills: form.skills,
        softwares: form.softwares,
        new_missions: form.new_missions,
        languages: form.languages,
        address: form.address,
        notes: form.notes,
        urgent: form.urgent,
        hourly_rate: parseFloat(form.hourly_rate) || 0,
      };
      const { data } = editing
        ? await api.patch(`/needs/${existing.id}`, payload)
        : await api.post("/needs", payload);
      onCreated?.(data);
      onClose();
      setForm(defaultForm());
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto" data-testid="new-need-modal">
      <div className="bg-white rounded-3xl w-full max-w-4xl border border-[#E2E5E0] shadow-2xl my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#E2E5E0]">
          <div>
            <h3 className="text-xl font-medium" style={{ fontFamily: "Outfit" }}>
              {editing ? "Modifier le besoin" : "Nouveau besoin"}
            </h3>
            <p className="text-xs text-[#6B726A] mt-0.5">Sélectionnez vos dates et définissez vos plages horaires.</p>
          </div>
          <button onClick={onClose} data-testid="close-new-need-modal" className="w-9 h-9 rounded-full hover:bg-[#F0F2EF] grid place-items-center">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 sm:p-8 space-y-7">
          {/* Mode picker */}
          <Field label="Type de besoin">
            <div className="grid sm:grid-cols-2 gap-3" data-testid="mode-picker">
              <ModeCard selected={form.mode === "permanent"}
                onClick={() => {
                  if (!editing && onSwitchMode) { onSwitchMode("permanent"); return; }
                  setForm((f) => ({ ...f, mode: "permanent" }));
                }}
                title="Permanent" desc="Poste durable : CDI, CDD, alternance — jours récurrents." testid="mode-permanent" />
              <ModeCard selected={form.mode === "temporary"} onClick={() => setForm((f) => ({ ...f, mode: "temporary" }))}
                title="Temporaire" desc="Remplacement ponctuel : dates précises sur un calendrier." testid="mode-temporary" />
            </div>
          </Field>

          {/* Day / Date selection */}
          {form.mode === "permanent" ? (
            <Field label="Jours de permanence">
              <div className="flex flex-wrap gap-2" data-testid="weekday-picker">
                {WEEKDAYS.map((w) => (
                  <button key={w.key} type="button" data-testid={`wday-${w.key}`}
                    onClick={() => toggleWeekday(w.key)}
                    className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                      form.weekdays.includes(w.key)
                        ? "bg-[#E8F0EA] border-[#1A4D2E] text-[#1A4D2E]"
                        : "border-[#E2E5E0] text-[#6B726A] hover:border-[#1A4D2E]/40"
                    }`}>{w.label}</button>
                ))}
              </div>
            </Field>
          ) : (
            <Field label="Sélectionnez les dates">
              <div className="rounded-2xl border border-[#E2E5E0] p-4 bg-white" data-testid="calendar-wrapper">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    <button type="button" data-testid="cal-prev"
                      onClick={() => setCalendarMonth((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
                      className="w-8 h-8 rounded-full border border-[#E2E5E0] hover:bg-[#F7F8F6] grid place-items-center">
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-sm font-semibold capitalize min-w-[140px] text-center" style={{ fontFamily: "Outfit" }} data-testid="cal-month-label">
                      {calendarMonth.toLocaleDateString(i18nLocale(), { month: "long", year: "numeric" })}
                    </span>
                    <button type="button" data-testid="cal-next"
                      onClick={() => setCalendarMonth((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
                      className="w-8 h-8 rounded-full border border-[#E2E5E0] hover:bg-[#F7F8F6] grid place-items-center">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    <button type="button" data-testid="cal-today"
                      onClick={() => setCalendarMonth(new Date())}
                      className="px-3 py-1.5 text-xs rounded-full border border-[#E2E5E0] hover:bg-[#F7F8F6]">Aujourd&apos;hui</button>
                  </div>
                  <button type="button" data-testid="cal-add"
                    onClick={() => { if (pending.size === 0) { setErr("Cliquez d'abord sur des jours dans le calendrier."); return; } setErr(""); setHoursOpen(true); }}
                    className="px-4 py-1.5 text-xs rounded-full bg-[#1A4D2E] text-white hover:bg-[#143C24] inline-flex items-center gap-1">
                    <Plus className="w-3.5 h-3.5" /> Ajouter
                  </button>
                </div>
                <div className="grid grid-cols-7 gap-1 mb-1">
                  {CAL_WD.map((w) => (
                    <div key={w} className="text-center text-[11px] uppercase tracking-wider font-semibold text-[#6B726A] py-1">{w}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1" data-testid="cal-grid">
                  {monthCells.map(({ date, muted }, i) => {
                    const k = isoDate(date);
                    const isPast = k < todayIso;
                    const isToday = k === todayIso;
                    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                    const slot = form.per_day[k]?.slots?.[0];
                    const committed = form.dates.includes(k);
                    const isPend = pending.has(k);
                    const bg = muted
                      ? "bg-[#FAFAFA] text-[#C5CBC2]"
                      : committed
                        ? "bg-[#E6F4EB] border border-[#A8D9B6] text-[#0F2A1B]"
                        : isWeekend
                          ? "bg-[#FFF2EE] text-[#A53A2F] border border-[#F5BFBA]"
                          : "bg-[#F2FAF4] text-[#0F2A1B] border border-[#E2E5E0]";
                    return (
                      <button key={`${k}-${i}`} type="button" data-testid={`cal-day-${k}`}
                        disabled={muted || isPast}
                        onClick={() => togglePending(k)}
                        className={`relative min-h-[64px] rounded-xl text-sm transition-all text-left p-2
                          ${bg}
                          ${isPend ? "ring-2 ring-[#1A4D2E] ring-offset-1" : ""}
                          ${isToday && !isPend ? "ring-1 ring-[#1A4D2E]/60" : ""}
                          ${(isPast || muted) ? "cursor-not-allowed opacity-60" : "hover:brightness-95"}`}>
                        <div className="font-medium">{date.getDate()}</div>
                        {committed && slot && (
                          <>
                            <span
                              role="button"
                              data-testid={`remove-date-${k}`}
                              title="Retirer cette date"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeDate(k); }}
                              className="absolute top-1 right-1 w-5 h-5 rounded-full grid place-items-center text-[#E3655B] hover:bg-[#FFF4F3]">
                              <Trash2 className="w-3 h-3" />
                            </span>
                            <div className="mt-1">
                              <div className="w-4 h-4 rounded-full bg-[#1A8F4A] grid place-items-center mb-0.5">
                                <CheckCircle2 className="w-2.5 h-2.5 text-white" />
                              </div>
                              <div className="text-[9px] text-[#1A4D2E] font-medium leading-tight">{slot.start} - {slot.end}</div>
                            </div>
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-3 pt-3 border-t border-dashed border-[#E2E5E0] flex flex-wrap gap-4 text-[11px] text-[#6B726A]">
                  <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#1A8F4A]" /> Date avec horaires</span>
                  <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full ring-2 ring-[#1A4D2E] bg-white" /> Sélection en cours</span>
                  <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#E3655B]" /> Week-end</span>
                </div>
              </div>
              <p className="text-xs text-[#6B726A] mt-2" data-testid="cal-hint">
                {pending.size > 0
                  ? `${pending.size} jour(s) sélectionné(s) — cliquez sur « Ajouter » pour définir les horaires`
                  : `${form.dates.length} date(s) avec horaires — cliquez sur des jours puis « Ajouter »`}
              </p>
              <div className="flex flex-wrap gap-2 mt-3" data-testid="cal-quick-actions">
                <button type="button" onClick={quickWeek} data-testid="qa-week"
                  className="px-3 py-1.5 text-xs rounded-full border border-[#1A8F4A]/40 text-[#1A8F4A] hover:bg-[#E6F4EB] inline-flex items-center gap-1">
                  <CalendarCheck className="w-3.5 h-3.5" /> Toute la semaine
                </button>
                <button type="button" onClick={quickMonth} data-testid="qa-month"
                  className="px-3 py-1.5 text-xs rounded-full border border-[#1A8F4A]/40 text-[#1A8F4A] hover:bg-[#E6F4EB] inline-flex items-center gap-1">
                  <CalendarCheck className="w-3.5 h-3.5" /> Tout le mois
                </button>
                <button type="button" onClick={resetMonth} data-testid="qa-reset"
                  className="px-3 py-1.5 text-xs rounded-full border border-[#E2E5E0] text-[#6B726A] hover:bg-[#F7F8F6] inline-flex items-center gap-1">
                  <RefreshCcw className="w-3.5 h-3.5" /> Réinitialiser le mois
                </button>
              </div>
              {form.dates.length > 0 && matchPreview !== null && (
                <div
                  data-testid="match-preview-badge"
                  className={`mt-3 inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full ${
                    matchPreview > 0 ? "bg-[#E8F0EA] text-[#1A4D2E]" : "bg-[#FEF3C7] text-[#92400E]"
                  }`}
                >
                  {matchPreview > 0 ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                  {matchPreview > 0
                    ? t("needs_page.preview_available", { count: matchPreview })
                    : t("needs_page.preview_none")}
                </div>
              )}
            </Field>
          )}

          {/* Selected days list with inline slot editors (mode permanent uniquement) */}
          {form.mode === "permanent" && dayKeys.length > 0 && (
            <Field label="Plages horaires par jour">
              <div className="space-y-3" data-testid="per-day-list">
                {dayKeys.map((key) => {
                  const slots = slotsFor(key);
                  const others = dayKeys.filter((k) => k !== key);
                  const isFocused = focusedKey === key;
                  const isTemporary = form.mode === "temporary";
                  return (
                    <div key={key}
                      data-day-card={key}
                      data-testid={`day-card-${key}`}
                      className={`border rounded-2xl p-4 bg-white transition-all ${
                        isFocused ? "border-[#1A4D2E] ring-2 ring-[#1A4D2E]/20 shadow-sm rp-flash" : "border-[#E2E5E0]"
                      }`}>
                      <div className="flex items-start justify-between mb-3 gap-2 flex-wrap">
                        <div className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-[#1A4D2E]" />
                          <span className="font-medium text-sm capitalize">{dayLabel(key)}</span>
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {others.length > 0 && (
                            <button type="button" data-testid={`apply-others-${key}`}
                              onMouseDown={(e) => { e.preventDefault(); document.activeElement?.blur?.(); }}
                              onClick={() => setApplyOpen(applyOpen === key ? null : key)}
                              className="text-xs text-[#1A4D2E] inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[#1A4D2E]/30 hover:bg-[#E8F0EA]">
                              <Copy className="w-3 h-3" /> Dupliquer
                            </button>
                          )}
                          {isTemporary && (
                            <button type="button"
                              data-testid={`remove-date-${key}`}
                              onClick={() => {
                                setForm((f) => {
                                  const per_day = { ...f.per_day };
                                  delete per_day[key];
                                  return { ...f, dates: f.dates.filter((d) => d !== key), per_day };
                                });
                                if (focusedKey === key) setFocusedKey(null);
                              }}
                              title="Retirer cette date"
                              className="text-xs text-[#E3655B] inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[#E3655B]/30 hover:bg-[#FFF4F3]">
                              <Trash2 className="w-3 h-3" /> Supprimer
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        {slots.map((s, idx) => (
                          <div key={idx} className="flex items-center gap-2" data-testid={`slot-${key}-${idx}`}>
                            <Pencil className="w-3.5 h-3.5 text-[#6B726A] shrink-0" />
                            <input type="time" className="rp-input flex-1" value={s.start}
                              data-testid={`slot-${key}-${idx}-start`}
                              onChange={(e) => updateSlot(key, idx, "start", e.target.value)} />
                            <span className="text-[#6B726A] text-sm">→</span>
                            <input type="time" className="rp-input flex-1" value={s.end}
                              data-testid={`slot-${key}-${idx}-end`}
                              onChange={(e) => updateSlot(key, idx, "end", e.target.value)} />
                            <button type="button" onClick={() => removeSlot(key, idx)}
                              data-testid={`remove-slot-${key}-${idx}`}
                              className="w-8 h-8 rounded-full hover:bg-[#FFF4F3] text-[#E3655B] grid place-items-center">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        <button type="button" onClick={() => addSlot(key)} data-testid={`add-slot-${key}`}
                          className="text-sm text-[#1A4D2E] inline-flex items-center gap-1 hover:underline">
                          <Plus className="w-4 h-4" /> Ajouter une plage
                        </button>
                      </div>

                      {applyOpen === key && others.length > 0 && (
                        <BulkApplyPanel
                          fromKey={key}
                          targets={others}
                          dayLabel={dayLabel}
                          onApply={(picked) => applySlotsToOthers(key, picked)}
                          onCancel={() => setApplyOpen(null)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </Field>
          )}

          {/* Details */}
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Adresse de l'officine">
              <div className="relative">
                <MapPin className="w-4 h-4 absolute left-3 top-3.5 text-[#1A4D2E] pointer-events-none" />
                <input className="rp-input bg-[#F0F2EF] text-[#1f2421] font-medium cursor-not-allowed" style={{ paddingLeft: "2.4rem" }} data-testid="need-address"
                  value={form.address} readOnly tabIndex={-1}
                  placeholder="Renseignez l'adresse dans Mon officine" />
              </div>
              <div className="text-[11px] text-[#6B726A] mt-1">Récupérée automatiquement depuis votre profil — non modifiable</div>
            </Field>
            <Field label="Tarif brut (€ / heure)">
              <div className="relative">
                <Euro className="w-4 h-4 absolute left-3 top-3.5 text-[#6B726A] pointer-events-none" />
                <input type="number" min={0} step="0.5" className="rp-input" style={{ paddingLeft: "2.4rem" }} data-testid="need-rate"
                  value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} />
              </div>
            </Field>
          </div>

          <Field label="Type de besoin">
            <div className="flex gap-2 flex-wrap">
              {ROLES.map((r) => (
                <button key={r} type="button" data-testid={`role-${r}`}
                  onClick={() => setForm({ ...form, role_type: r })}
                  className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                    form.role_type === r
                      ? "bg-[#E8F0EA] border-[#1A4D2E] text-[#1A4D2E]"
                      : "border-[#E2E5E0] text-[#6B726A] hover:border-[#1A4D2E]/40"
                  }`}>{r}</button>
              ))}
            </div>
          </Field>

          <div>
            <div className="text-sm font-semibold mb-3" style={{ fontFamily: "Outfit" }}>Compétences & logiciels</div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-[#6B726A] mb-2">Maîtrise des logiciels *</div>
                <div className="grid grid-cols-2 gap-2" data-testid="need-softwares">
                  {SOFTWARES.map((s) => (
                    <NeedCheckbox key={s} label={s} testid={`need-software-${s}`}
                      checked={form.softwares.includes(s)} onChange={() => toggleIn("softwares", s)} />
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-[#6B726A] mb-2">Nouvelles missions du pharmacien</div>
                <div className="grid grid-cols-1 gap-2" data-testid="need-missions">
                  {NEW_MISSIONS.map((m) => (
                    <NeedCheckbox key={m} label={m} testid={`need-mission-${m}`}
                      checked={form.new_missions.includes(m)} onChange={() => toggleIn("new_missions", m)} />
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-5">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#6B726A] mb-2">Langues parlées *</div>
              <div className="flex flex-wrap gap-2" data-testid="need-languages">
                {LANG_CODES.map((c) => (
                  <button key={c} type="button" data-testid={`need-lang-${c}`}
                    onClick={() => toggleIn("languages", c)}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                      form.languages.includes(c)
                        ? "bg-[#E8F0EA] border-[#1A4D2E] text-[#1A4D2E]"
                        : "border-[#E2E5E0] text-[#6B726A] hover:border-[#1A4D2E]/40"
                    }`}>{langName(c, i18n.language)}</button>
                ))}
              </div>
            </div>
          </div>

          <Field label="Notes additionnelles">
            <textarea rows={2} className="rp-input resize-none" value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Précisions pour les candidats…" />
          </Field>

          <label className="flex items-start gap-3 p-4 rounded-2xl border border-[#E2E5E0] cursor-pointer hover:border-[#E3655B]/40">
            <input type="checkbox" data-testid="need-urgent"
              checked={form.urgent} onChange={(e) => setForm({ ...form, urgent: e.target.checked })}
              className="mt-1 accent-[#E3655B]" />
            <div className="flex-1">
              <div className="font-medium text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-[#E3655B]" /> Marquer comme urgent
              </div>
              <div className="text-xs text-[#6B726A]">Active une notification immédiate aux candidats à proximité.</div>
            </div>
          </label>

          {err && (
            <div className="text-sm text-[#E3655B] bg-[#FFF4F3] border border-[#E3655B]/20 rounded-xl px-3 py-2" data-testid="need-error">
              {err}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-t border-[#E2E5E0] sticky bottom-0 bg-white rounded-b-3xl">
          <button onClick={onClose} data-testid="need-cancel-btn" className="text-sm text-[#6B726A] hover:text-[#1f2421]">
            Annuler
          </button>
          <button onClick={submit} disabled={busy} data-testid="submit-new-need"
            className="rp-btn-primary flex items-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Enregistrer
          </button>
        </div>
      </div>

      {hoursOpen && (
        <AddHoursModal
          selectedDates={Array.from(pending).sort()}
          dayLabel={dayLabel}
          onClose={() => setHoursOpen(false)}
          onConfirm={applyHours}
        />
      )}

      <style>{`
        .rp-input{width:100%;background:#fff;border:1px solid #E2E5E0;border-radius:.75rem;padding:.55rem .85rem;outline:none;font-size:.9rem}
        .rp-input:focus{border-color:#1A4D2E;box-shadow:0 0 0 3px rgba(26,77,46,.12)}
        .rp-btn-primary{background:#1A4D2E;color:#fff;border-radius:9999px;padding:.6rem 1.25rem;font-weight:500;font-size:.9rem}
        .rp-btn-primary:hover{background:#143C24}
        .rdp{--rdp-cell-size:38px;--rdp-accent-color:#1A4D2E;--rdp-background-color:#E8F0EA;margin:0}
        .rdp-day_selected{background:#1A4D2E !important;color:#fff !important;border-radius:9999px !important;font-weight:600 !important}
        .rdp-day_selected:hover{background:#143C24 !important}
        .rdp-today-bold{font-weight:700;text-decoration:underline}
        @keyframes rp-flash-anim {
          0%   { box-shadow: 0 0 0 0 rgba(26,77,46,0.5); }
          50%  { box-shadow: 0 0 0 6px rgba(26,77,46,0); }
          100% { box-shadow: 0 0 0 0 rgba(26,77,46,0); }
        }
        .rp-flash { animation: rp-flash-anim 600ms ease-out; }
      `}</style>
    </div>
  );
}

function AddHoursModal({ selectedDates, dayLabel, onClose, onConfirm }) {
  const [type, setType] = useState("full");
  const [customStart, setCustomStart] = useState("09:00");
  const [customEnd, setCustomEnd] = useState("17:00");
  const start = type === "custom" ? customStart : TYPE_HOURS[type].start;
  const end = type === "custom" ? customEnd : TYPE_HOURS[type].end;
  const first = selectedDates[0];
  const last = selectedDates[selectedDates.length - 1];
  const rangeLabel = first && last && first !== last
    ? `Du ${dayLabel(first)} au ${dayLabel(last)}`
    : (first ? dayLabel(first) : "");
  return (
    <div className="fixed inset-0 bg-black/30 grid place-items-center z-[60] p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="add-hours-modal">
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-base font-medium text-[#0F2A1B]" style={{ fontFamily: "Outfit" }}>Définir les horaires</h3>
          <button onClick={onClose} className="w-7 h-7 grid place-items-center text-[#6B726A] hover:text-[#0F2A1B]" data-testid="hours-modal-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="text-xs text-[#6B726A] mb-4">
          <div>{selectedDates.length} {selectedDates.length > 1 ? "jours sélectionnés" : "jour sélectionné"}</div>
          {rangeLabel && <div className="font-medium text-[#0F2A1B] mt-0.5 capitalize">{rangeLabel}</div>}
        </div>

        <div className="text-xs uppercase tracking-wider font-medium text-[#6B726A] mb-2">Type d&apos;horaires</div>
        <div className="space-y-1 mb-4">
          {["full","morning","afternoon","custom"].map((k) => (
            <label key={k} className={`flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border cursor-pointer ${type === k ? "border-[#1A4D2E] bg-[#E8F0EA]" : "border-[#E2E5E0] hover:bg-[#F7F8F6]"}`} data-testid={`hours-type-${k}`}>
              <span className="flex items-center gap-2 text-sm text-[#0F2A1B]">
                <span className={`w-4 h-4 rounded-full border-2 grid place-items-center ${type === k ? "border-[#1A4D2E]" : "border-[#C5CBC2]"}`}>
                  {type === k && <span className="w-2 h-2 rounded-full bg-[#1A4D2E]" />}
                </span>
                {k === "custom" ? "Personnalisée" : TYPE_HOURS[k]?.label}
              </span>
              <span className="text-xs text-[#6B726A]">{k === "custom" ? `${customStart} - ${customEnd}` : `${TYPE_HOURS[k].start} - ${TYPE_HOURS[k].end}`}</span>
              <input type="radio" name="need-hours-type" className="hidden" checked={type === k} onChange={() => setType(k)} />
            </label>
          ))}
        </div>

        {type === "custom" && (
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs text-[#6B726A]">De</span>
            <input type="time" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="text-xs px-2 py-1 rounded border border-[#E2E5E0] flex-1" data-testid="custom-start" />
            <span className="text-xs text-[#6B726A]">à</span>
            <input type="time" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="text-xs px-2 py-1 rounded border border-[#E2E5E0] flex-1" data-testid="custom-end" />
          </div>
        )}

        <div className="text-xs px-3 py-2 rounded-lg bg-[#F4F0FF] border border-[#D9CCFB] text-[#3F2EB2] mb-4 inline-flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> Ces horaires seront appliqués aux dates sélectionnées
        </div>

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 text-sm px-4 py-2.5 rounded-full border border-[#E2E5E0] text-[#6B726A] hover:bg-[#F7F8F6]" data-testid="hours-modal-cancel">Annuler</button>
          <button onClick={() => onConfirm({ type, start, end })} disabled={selectedDates.length === 0} data-testid="hours-modal-confirm"
            className="flex-1 text-sm px-4 py-2.5 rounded-full bg-[#1A4D2E] text-white hover:bg-[#143C24] disabled:opacity-60">
            Ajouter
          </button>
        </div>
      </div>
    </div>
  );
}

function NeedCheckbox({ label, checked, onChange, testid }) {
  return (
    <label data-testid={testid} className="flex items-center gap-2 text-sm cursor-pointer select-none">
      <input type="checkbox" checked={checked} onChange={onChange} className="accent-[#1A4D2E] w-4 h-4 rounded" />
      <span className={checked ? "text-[#1f2421]" : "text-[#6B726A]"}>{label}</span>
    </label>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider font-semibold text-[#6B726A]">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

function ModeCard({ selected, onClick, title, desc, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`text-left p-4 rounded-2xl border-2 transition-all ${
        selected ? "border-[#1A4D2E] bg-[#E8F0EA]" : "border-[#E2E5E0] hover:border-[#1A4D2E]/40"
      }`}>
      <div className="font-medium text-sm flex items-center gap-2 mb-1">
        <span className={`w-4 h-4 rounded-full border-2 ${selected ? "border-[#1A4D2E] bg-[#1A4D2E]" : "border-[#C5CBC2]"}`} />
        {title}
      </div>
      <div className="text-xs text-[#6B726A]">{desc}</div>
    </button>
  );
}

function BulkApplyPanel({ fromKey, targets, dayLabel, onApply, onCancel }) {
  const [picked, setPicked] = useState([]);
  const toggle = (k) => setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  return (
    <div className="mt-3 p-3 rounded-xl bg-[#F7F8F6] border border-[#E2E5E0]" data-testid={`bulk-apply-${fromKey}`}>
      <div className="text-xs text-[#6B726A] mb-2">Copier ces plages vers :</div>
      <div className="flex flex-wrap gap-2 mb-3">
        {targets.map((k) => (
          <button key={k} type="button"
            data-testid={`bulk-target-${k}`}
            onClick={() => toggle(k)}
            className={`px-3 py-1 rounded-full text-xs border transition-colors capitalize ${
              picked.includes(k)
                ? "bg-[#E8F0EA] border-[#1A4D2E] text-[#1A4D2E]"
                : "border-[#E2E5E0] text-[#6B726A]"
            }`}>{dayLabel(k)}</button>
        ))}
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="text-xs text-[#6B726A]" data-testid={`bulk-cancel-${fromKey}`}>Annuler</button>
        <button onClick={() => onApply(picked)} disabled={picked.length === 0}
          data-testid={`bulk-apply-confirm-${fromKey}`}
          className="text-xs px-3 py-1 rounded-full bg-[#1A4D2E] text-white disabled:opacity-50">
          Appliquer ({picked.length})
        </button>
      </div>
    </div>
  );
}

// (frLocale imported from date-fns/locale above)
